// A WebSocket that keeps itself connected: exponential backoff with jitter, an immediate retry
// when the phone wakes up or comes back online, and a heartbeat that replaces a silently dead
// socket (phones sleeping at night are the normal case, not the exception).
import type { ClientMsg, ServerMsg } from "../protocol";

export type SocketStatus = "idle" | "connecting" | "open" | "waiting" | "stopped";

export interface SocketHandlers {
  onOpen(): void;
  onMessage(msg: ServerMsg): void;
  onClose(code: number, reason: string): void;
  onStatus?(status: SocketStatus): void;
}

const BACKOFF_MIN_MS = 500;
const BACKOFF_MAX_MS = 8000;
const PING_EVERY_MS = 25_000;
const SILENCE_LIMIT_MS = 60_000;

export class ReconnectingSocket {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastHeard = 0;
  /** No reconnect before this time (epoch ms): the server asked us to wait. */
  private notBefore = 0;
  private status: SocketStatus = "idle";
  private readonly onWake = () => {
    if (document.visibilityState === "visible") this.nudge();
  };
  private readonly onOnline = () => this.nudge();

  constructor(
    private readonly url: string,
    private readonly handlers: SocketHandlers,
    private readonly makePing: () => ClientMsg,
  ) {}

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  start(): void {
    if (this.status !== "idle" && this.status !== "stopped") return;
    document.addEventListener("visibilitychange", this.onWake);
    window.addEventListener("online", this.onOnline);
    this.pingTimer = setInterval(() => this.heartbeat(), PING_EVERY_MS);
    this.connect();
  }

  /** Stop for good: no more reconnects until start() is called again. */
  stop(): void {
    this.setStatus("stopped");
    document.removeEventListener("visibilitychange", this.onWake);
    window.removeEventListener("online", this.onOnline);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.clearRetry();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close(1000, "bye");
      } catch {
        /* already closed */
      }
    }
  }

  send(msg: ClientMsg): boolean {
    if (!this.isOpen || !this.ws) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  /** The server asked us to wait: drop this socket and reconnect no sooner than `ms` from now. */
  retryIn(ms: number): void {
    this.notBefore = Date.now() + ms;
    try {
      this.ws?.close(1000, "retry later"); // its onclose schedules the reconnect
    } catch {
      /* already closed */
    }
  }

  /** Reconnect now if we are not connected (the phone woke up or the network came back). */
  nudge(): void {
    if (this.status === "stopped") return;
    if (this.isOpen) {
      this.send(this.makePing());
      return;
    }
    if (this.status === "waiting" && Date.now() >= this.notBefore) {
      this.clearRetry();
      this.connect();
    }
  }

  private connect(): void {
    this.clearRetry();
    this.setStatus("connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempts = 0;
      this.lastHeard = Date.now();
      this.setStatus("open");
      this.handlers.onOpen();
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      this.lastHeard = Date.now();
      let msg: ServerMsg;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMsg;
      } catch {
        return;
      }
      this.handlers.onMessage(msg);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.handlers.onClose(ev.code, ev.reason);
      if (this.status !== "stopped") this.scheduleRetry();
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private heartbeat(): void {
    if (!this.isOpen) return;
    if (Date.now() - this.lastHeard > SILENCE_LIMIT_MS) {
      // the socket looks open but nothing has arrived for a minute: replace it
      const ws = this.ws;
      this.ws = null;
      if (ws) {
        ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
        try {
          ws.close(4000, "silent");
        } catch {
          /* ignore */
        }
      }
      this.handlers.onClose(4000, "silent");
      this.scheduleRetry(true);
      return;
    }
    this.send(this.makePing());
  }

  private scheduleRetry(immediate = false): void {
    this.setStatus("waiting");
    this.clearRetry();
    const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** this.attempts);
    const backoff = immediate ? 0 : base / 2 + Math.random() * (base / 2);
    const delay = Math.max(backoff, this.notBefore - Date.now());
    this.attempts += 1;
    this.retryTimer = setTimeout(() => this.connect(), delay);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private setStatus(status: SocketStatus): void {
    this.status = status;
    this.handlers.onStatus?.(status);
  }
}

export function wsUrl(code: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws/${encodeURIComponent(code)}`;
}
